/**
 * Phase 1c — entity-linked liabilities and structure warnings.
 */
import {
    entityOwnershipValue,
    getEntityIdFromOwnership,
    isEntityOwnership,
} from '../../../shared/src/calculation/constants/clientStructureTypes.ts';
import { ASSET_TYPES } from '../../../shared/src/calculation/constants/assetTypes.ts';
import { calculateLoanRepayment } from '../../../shared/src/calculation/taxAndLoanUtils.ts';
import type { FactFindLiability } from '../../../shared/src/calculation/factFindTypes.ts';
import { buildApplicantVoice } from './applicantVoice.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';

type DraftLiability = FactFindLiability;

const LIQUID_ASSET_TYPES = new Set(
    ASSET_TYPES.filter((t) => t.isLiquid).map((t) => t.id)
);

/**
 * Re-exported from the shared calculation, not reimplemented.
 *
 * This file used to carry its own line-for-line copy of the annual repayment arithmetic. The two
 * agreed, but only by luck: nothing linked them, and a rate or frequency rule corrected on the engine
 * side would have left the fact-find quietly showing the client a different number from the one their
 * projection was built on. The same duplication is what let the repayment field's read-only rule drift
 * from the engine's in the first place.
 */
export {
    calculateAnnualLoanRepayment,
    derivesRepaymentAutomatically,
    isTermLoanType,
} from '../../../shared/src/calculation/support/structureLiabilitiesUtils.ts';

/**
 * Entity id if this liability belongs to a trust/company pool (explicit ownership or secured asset link).
 */
export function resolveLiabilityEntityId(liability: DraftLiability, factFind: DraftFactFind): string | null {
    if (isEntityOwnership(liability.ownership)) {
        const id = getEntityIdFromOwnership(liability.ownership);
        return id && id !== '__unsure__' ? id : null;
    }

    if (liability.type !== 'mortgages') return null;

    if (liability.assetLink === 'home' || liability.assetLink === 'none') {
        if (factFind.home?.ownership === 'other_entity' && factFind.home?.entityId) {
            return factFind.home.entityId;
        }
        return null;
    }

    const prop = (factFind.assets?.investmentProperty || []).find(
        (p) => String(p.id) === String(liability.assetLink)
    );
    if (prop && isEntityOwnership(prop.ownership)) {
        return getEntityIdFromOwnership(prop.ownership);
    }
    return null;
}

export function isEntityLiability(liability: DraftLiability, factFind: DraftFactFind): boolean {
    return resolveLiabilityEntityId(liability, factFind) != null;
}

export interface OwnershipOption {
    value: string;
    label: string;
}

export function buildOwnershipOptions(
    factFind: DraftFactFind,
    { individualOnly = false, isPartnered = false }: { individualOnly?: boolean; isPartnered?: boolean } = {},
): OwnershipOption[] {
    const options: OwnershipOption[] = [{ value: 'client', label: factFind.client?.firstName || 'Applicant 1' }];
    if (isPartnered) {
        options.push({ value: 'partner', label: factFind.partner?.firstName || 'Applicant 2' });
        if (!individualOnly) options.push({ value: 'joint', label: 'Joint' });
    }
    (factFind.structures || []).forEach((structure) => {
        options.push({
            value: entityOwnershipValue(structure.id ?? ''),
            label: structure.name?.trim() || 'Unnamed arrangement',
        });
    });
    return options;
}

export function isLiquidAssetType(typeId: string): boolean {
    return LIQUID_ASSET_TYPES.has(typeId);
}

/**
 * Warnings for Step 6 — e.g. trust-held property with loan not tagged to the entity.
 */
export function getStructureLiabilityWarnings(factFind: DraftFactFind): string[] {
    const warnings: string[] = [];
    const structures = factFind.structures || [];
    if (!structures.length) return warnings;

    const { possessive } = buildApplicantVoice(factFind);

    (factFind.assets?.investmentProperty || []).forEach((prop) => {
        if (!isEntityOwnership(prop.ownership)) return;
        const entityId = getEntityIdFromOwnership(prop.ownership);
        const label = prop.description || 'Investment property';
        const balance = prop.value || 0;
        if (balance <= 0) return;

        const mortgages = factFind.liabilities?.mortgages || [];
        const hasEntityLoan = mortgages.some((m) => resolveLiabilityEntityId(m, factFind) === entityId);
        const hasPersonalLoanOnAsset = mortgages.some(
            (m) =>
                String(m.assetLink) === String(prop.id) &&
                !isEntityOwnership(m.ownership) &&
                resolveLiabilityEntityId(m, factFind) !== entityId
        );

        if (!hasEntityLoan && !hasPersonalLoanOnAsset) {
            warnings.push(
                `${label} is held in a trust or company but no loan is recorded against that arrangement. Add the mortgage under Ownership for that trust/company, or net worth and pension estimates may overstate ${possessive} position.`
            );
        } else if (hasPersonalLoanOnAsset) {
            warnings.push(
                `${label} is held in a trust or company but the linked mortgage is in a personal name. For accurate figures, set the loan Ownership to the same trust or company.`
            );
        }
    });

    return warnings;
}
